import { ArrowDownRight, ArrowUpRight, ArrowsClockwise, FileCsv, Lightning, LockSimple, Play, Receipt, Trash, Upload, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { ColumnsMenu } from "@/components/ColumnsMenu";
import { DealListTabs } from "@/components/deals/DealListTabs";
import { DealRow } from "@/components/deals/DealRow";
import { Kpi, Th, sortRows } from "@/components/deals/tableBits";
import { ManualOfferDialog } from "@/components/deals/ManualOfferDialog";
import { ManualPriceDialog } from "@/components/deals/ManualPriceDialog";
import { OfferExportDialog } from "@/components/deals/OfferExportDialog";
import { OfferRulesPanel } from "@/components/deals/OfferRulesPanel";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { money } from "@/components/deals/format";

export default function DealsTable() {
  const [tab, setTab] = useState("active");
  const [lists, setLists] = useState([]);
  const [listId, setListId] = useState(() => localStorage.getItem("deals_active_list") || null);
  const [data, setData] = useState({ rows: [], summary: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [replaceOnUpload, setReplaceOnUpload] = useState(false);
  const [scanJob, setScanJob] = useState(null);
  const [scanContinuous, setScanContinuous] = useState(false);
  // Whole-list scans skip rows that already have data for that source (saves
  // Keepa tokens). Ticked-row scans always force a refresh.
  const [missingOnly, setMissingOnly] = useState(() => localStorage.getItem("deals_missing_only") !== "false");
  const toggleMissingOnly = (v) => { setMissingOnly(v); localStorage.setItem("deals_missing_only", String(v)); };
  const [selected, setSelected] = useState(new Set());  // set of set_numbers ticked in the table
  const [settings, setSettings] = useState(null);
  const [offersOpen, setOffersOpen] = useState(false);
  // Single hoisted state for the manual-price dialog — any DealRow can
  // trigger it via openManualPrice() below.
  const [manualState, setManualState] = useState({ open: false, field: null, setNumber: null, currentValue: null, isManual: false, currentNote: null });
  const [offerEdit, setOfferEdit] = useState({ open: false, dealId: null, setNumber: null, auto: null, current: null, isManual: false });
  const openManualPrice = (field, setNumber, currentValue, isManual, currentNote) =>
    setManualState({ open: true, field, setNumber, currentValue, isManual, currentNote });
  // Column visibility — persisted in localStorage so the user's layout survives reload.
  const ALL_COLS = [
    { key: "name", label: "Name" },
    { key: "asin", label: "ASIN" },
    { key: "qty", label: "Qty" },
    { key: "paid", label: "Paid" },
    { key: "amz_bb", label: "AMZ Buy Box" },
    { key: "fba_fbm", label: "FBA/FBM" },
    { key: "stock", label: "Stock" },
    { key: "rank", label: "Rank" },
    { key: "bl_lowest", label: "BL Lowest" },
    { key: "bl_avg", label: "BL Avg" },
    { key: "pc_sealed", label: "PC Sealed" },
    { key: "suggested", label: "Suggest/Max Buy" },
    { key: "smart_offer", label: "Smart Offer" },
    { key: "approve", label: "Approve" },
    { key: "unit_pl", label: "Unit P&L" },
    { key: "last_scan", label: "Last Scan" },
  ];
  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("deals_visible_cols") || "null");
      if (Array.isArray(saved)) return new Set(saved);
    } catch {}
    return new Set(ALL_COLS.map(c => c.key));
  });
  const toggleCol = (key) => setVisibleCols(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    try { localStorage.setItem("deals_visible_cols", JSON.stringify([...next])); } catch {}
    return next;
  });
  const isVisible = (k) => visibleCols.has(k);
  const fileRef = useRef(null);
  // Column sort — click header to toggle asc/desc; persisted per browser.
  const [sort, setSort] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem("deals_sort") || "null"); if (s?.key) return s; } catch {}
    return { key: null, dir: "asc" };
  });
  const onSort = (key) => setSort(prev => {
    const next = prev.key === key ? (prev.dir === "asc" ? { key, dir: "desc" } : { key: null, dir: "asc" }) : { key, dir: "asc" };
    try { localStorage.setItem("deals_sort", JSON.stringify(next)); } catch {}
    return next;
  });
  const sortedRows = sortRows(data.rows, sort);

  const loadLists = async () => {
    try {
      const ls = await api.listDealLists();
      setLists(ls);
      if (!ls.find(l => l.id === listId) && ls[0]) setListId(ls[0].id);
      return ls;
    } catch { toast.error("Failed to load deal tabs"); return []; }
  };
  const selectList = (id) => { setListId(id); localStorage.setItem("deals_active_list", id); };

  const load = async (silent = false) => {
    if (!listId) return;
    if (!silent) setLoading(true);
    try { setData(await api.listDeals(tab, 5000, listId)); } catch { toast.error("Failed to load deals"); }
    setLoading(false);
  };
  const refresh = () => load(true); // in-place refresh — keeps the table mounted + scroll position

  useEffect(() => { loadLists(); }, []);
  useEffect(() => { load(); setSelected(new Set()); setScanJob(null); }, [tab, listId]);
  useEffect(() => { api.getSettings().then(setSettings).catch(() => {}); }, []);
  const activeList = lists.find(l => l.id === listId);

  // Poll job progress
  useEffect(() => {
    if (!scanJob) return;
    if (["completed", "failed", "paused"].includes(scanJob.status)) return;
    const id = setInterval(async () => {
      try {
        const j = await api.getScan(scanJob.id);
        setScanJob(j);
        if (["completed", "failed", "paused"].includes(j.status)) refresh();
      } catch (e) { /* poll error, keep trying */ }
    }, 2000);
    return () => clearInterval(id);
  }, [scanJob]);

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const r = await api.uploadDeals(f, tab, replaceOnUpload, true, listId);
      toast.success(
        `Imported ${r.inserted} deals (${r.skipped} skipped)` +
        (r.asins_saved_to_catalog ? ` · ${r.asins_saved_to_catalog} ASINs saved to catalog` : "") +
        (r.pricecharting_job ? ` · fetching PriceCharting for ${r.pricecharting_job.total} sets` : "")
      );
      await load(); loadLists();
      if (r.pricecharting_job) {
        try { setScanJob(await api.getScan(r.pricecharting_job.id)); } catch { /* job poll will retry */ }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const startScan = async (source, onlySelected = false, opts = {}) => {
    if (!data.rows.length) { toast.error("Upload deals first"); return; }
    // Build the list of set_numbers to scan (from checkboxes if the user
    // hit "SCAN SELECTED", otherwise pass null = scan every deal).
    let setNumbers = null;
    if (onlySelected) {
      if (selected.size === 0) { toast.error("Tick at least one row first"); return; }
      setNumbers = Array.from(selected);
    }
    const refresh = !!opts.asinRowsOnly;
    try {
      const j = await api.scanDeals(source, tab, scanContinuous, {
        setNumbers, listId, missingOnly: !onlySelected && !refresh && missingOnly, asinRowsOnly: refresh,
      });
      setScanJob(j);
      const srcLabel = source.replace("_", " ").toUpperCase();
      const label = refresh
        ? `Buy Box for ${j.total} ASIN rows (skipping No-ASIN / Not-on-Amazon)`
        : onlySelected
          ? `${setNumbers.length} selected`
          : (missingOnly ? `${j.total} of ${data.rows.length} deals missing ${srcLabel} data` : `${data.rows.length} deals`);
      toast.info(`${refresh ? "Refreshing" : "Scanning"} ${label} via ${srcLabel}…`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Scan start failed");
    }
  };

  // Checkbox helpers
  const toggleRow = (sn) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(sn)) next.delete(sn);
      else next.add(sn);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected(prev => {
      const all = data.rows.map(r => r.set_number).filter(Boolean);
      const allSelected = all.length > 0 && all.every(sn => prev.has(sn));
      return allSelected ? new Set() : new Set(all);
    });
  };
  const clearSelection = () => setSelected(new Set());

  const cancelScan = async () => {
    if (!scanJob) return;
    try { await api.cancelDealsScan(scanJob.id); toast.info("Cancelling…"); } catch (e) { /* ignore */ }
  };

  const [pushingBoard, setPushingBoard] = useState(false);
  const [approvingAll, setApprovingAll] = useState(false);
  // Rows whose live offer is the SMART OFFER and aren't locked yet (selection narrows it)
  const smartCandidates = data.rows.filter(r =>
    r.set_number && r.offer_is_smart && r.effective_offer > 0 && !(r.offer_approved && r.approved_offer != null)
    && (selected.size === 0 || selected.has(r.set_number)));
  const approveAllSmart = async () => {
    if (!smartCandidates.length) return;
    setApprovingAll(true);
    try {
      const r = await api.approveDealsBulk(smartCandidates.map(x => ({ id: x.id, offer: x.effective_offer })));
      toast.success(`${r.approved} Smart Offer${r.approved === 1 ? "" : "s"} approved`);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk approve failed"); }
    setApprovingAll(false);
  };
  const pushToBoard = async () => {
    setPushingBoard(true);
    try {
      const r = await api.pushDealsToBoard(listId);
      toast.success(`Board updated · ${r.amazon_pushed} Amazon + ${r.bricklink_pushed} BrickLink prices across ${r.sets} sets`);
    } catch (e) { toast.error(e.response?.data?.detail || "Push failed"); }
    setPushingBoard(false);
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this deal?")) return;
    try { await api.deleteDeal(id); toast.success("Deleted"); refresh(); loadLists(); }
    catch { toast.error("Delete failed"); }
  };

  const onClearAll = async () => {
    if (!window.confirm(`Clear ALL ${tab} deals in "${activeList?.name || "this tab"}"? This can't be undone.`)) return;
    try { await api.clearDeals(tab, listId); toast.success(`${tab} deals cleared`); load(); loadLists(); }
    catch { toast.error("Clear failed"); }
  };

  const summary = data.summary || {};
  const scanRunning = scanJob && ["running", "queued", "waiting_for_tokens"].includes(scanJob.status);
  const progressPct = scanJob && scanJob.total ? Math.round((scanJob.progress / scanJob.total) * 100) : 0;

  return (
    <div className="p-6 space-y-6 max-w-[1600px]">
      <div className="flex items-center gap-3">
        <Receipt size={22} weight="bold" className="text-[#E3000B]" />
        <h1 className="font-head text-2xl font-black uppercase tracking-tighter" data-testid="deals-title">Deals</h1>
        <span className="font-mono text-[10px] text-gray-500 ml-2">upload your Amazon seller sheet — scan Amazon, BrickLink & PriceCharting per set</span>
      </div>

      {/* Deal list tabs (Chrome-style) */}
      <DealListTabs lists={lists} activeId={listId} onSelect={selectList} onChanged={loadLists} />

      {/* Active / Archive sub-tabs — scoped to the selected deal list */}
      <div className="flex items-center gap-2 border-b border-[#E4E4E0]">
        {[
          { id: "active", label: "ACTIVE", hint: "deals you're working right now" },
          { id: "archive", label: "ARCHIVE", hint: "past completed deals" },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`deals-tab-${t.id}`}
            className={`px-4 h-9 border-b-2 font-head text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition-colors ${
              tab === t.id ? "border-[#E3000B] text-[#E3000B]" : "border-transparent text-gray-500 hover:text-black"
            }`}>
            {t.label}
            <span className="font-mono text-[9px] text-gray-400 ml-1 normal-case tracking-normal">{t.hint}</span>
          </button>
        ))}
        {activeList && (
          <span className="ml-auto font-mono text-[10px] text-gray-500 pr-2" data-testid="deals-active-list-label">
            TAB: <span className="text-[#E3000B]">{activeList.name}</span>
          </span>
        )}
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="deals-kpi">
        <Kpi label="Rows" value={data.total ?? 0} tone="text-gray-900" />
        <Kpi label="Total Cost" value={money(summary.total_cost)} tone="text-gray-800" />
        {tab === "archive" ? (
          <>
            <Kpi label="Total Sold For" value={money(summary.total_sale)} tone="text-gray-800" />
            <Kpi label="Realized P&L" value={money(summary.realized_pl)}
              tone={(summary.realized_pl ?? 0) >= 0 ? "text-[#00A850]" : "text-[#FF3333]"}
              icon={(summary.realized_pl ?? 0) >= 0 ? ArrowUpRight : ArrowDownRight} />
          </>
        ) : (
          <>
            <Kpi label="Current AMZ Value" value={money(summary.unrealized_current_value)} tone="text-gray-800" />
            <Kpi label="Total Offer" value={money(summary.total_offer)} tone="text-[#E3000B]"
              hint={`${summary.offer_rows ?? 0} offers · ${summary.manual_offer_rows ?? 0} adjusted · ${summary.approved_rows ?? 0} approved`} testId="deals-kpi-total-offer" />
            <Kpi label="Approved Total" value={money(summary.total_approved)} tone="text-[#00A850]"
              hint={`${summary.approved_rows ?? 0} locked offers`} testId="deals-kpi-approved" icon={LockSimple} />
            <Kpi label="Unrealized P&L" value={money(summary.unrealized_pl)}
              tone={(summary.unrealized_pl ?? 0) >= 0 ? "text-[#00A850]" : "text-[#FF3333]"}
              icon={(summary.unrealized_pl ?? 0) >= 0 ? ArrowUpRight : ArrowDownRight} />
          </>
        )}
      </div>

      {/* Overflow warning — makes silently-hidden rows impossible */}
      {data.total > data.rows.length && (
        <div
          className="border border-[#FF8C00]/60 bg-[#FF8C00]/[0.06] rounded-sm p-3 flex items-center justify-between"
          data-testid="deals-overflow-warning"
        >
          <div className="font-mono text-xs text-[#FF8C00]">
            Showing <span className="text-gray-900">{data.rows.length}</span> of <span className="text-gray-900">{data.total}</span> deals — <span className="text-gray-900">{data.total - data.rows.length}</span> hidden. Contact the developer to increase the page limit or narrow the tab.
          </div>
        </div>
      )}

      {/* Upload row */}
      <div className="border border-[#E4E4E0] rounded-sm p-4 flex items-center flex-wrap gap-4" data-testid="deals-upload-bar">
        <FileCsv size={22} className="text-[#E3000B]" weight="bold" />
        <div className="flex-1 min-w-[220px]">
          <div className="font-head text-sm font-bold uppercase tracking-wider">Upload {tab} deals CSV</div>
          <div className="text-[10px] font-mono text-gray-500 mt-0.5">
            Columns auto-detected: <span className="text-gray-800">ASIN, Listing Title, Buy Cost, Sold For, Qty, Date Purchased, SKU</span>
          </div>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <Switch checked={replaceOnUpload} onCheckedChange={setReplaceOnUpload} data-testid="deals-replace-toggle" />
          <span className="font-mono text-[10px] uppercase tracking-wider text-gray-600">Replace existing</span>
        </label>
        <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="deals-upload-btn"
          className="h-9 bg-[#E3000B] text-white font-bold uppercase tracking-wider hover:bg-[#E6B800] rounded-sm">
          <Upload size={12} weight="bold" className="mr-1" />
          {uploading ? "UPLOADING…" : "UPLOAD CSV"}
        </Button>
        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onUpload} data-testid="deals-file-input" />
        {data.total > 0 && (
          <Button onClick={onClearAll} variant="outline" className="h-9 border-[#FF3333] text-[#FF3333] hover:bg-[#FF3333]/10 font-mono text-xs uppercase rounded-sm" data-testid="deals-clear-all">
            <Trash size={12} weight="bold" className="mr-1" /> CLEAR ALL
          </Button>
        )}
      </div>

      {/* Scan control panel */}
      <div className="border border-[#E4E4E0] rounded-sm">
        <div className="px-4 py-3 border-b border-[#E4E4E0] flex items-center gap-2">
          <Lightning size={14} weight="fill" className="text-[#E3000B]" />
          <span className="font-head font-bold uppercase text-xs tracking-wider text-gray-600">Scans</span>
          <span className="text-[10px] font-mono text-gray-500 ml-2 normal-case tracking-normal">
            Run one source at a time — writes back to every deal row for that set
          </span>
        </div>
        <div className="p-4 space-y-3">
          <div className="flex items-center flex-wrap gap-2">
            <Button onClick={() => startScan("amazon")} disabled={scanRunning || !data.rows.length}
              data-testid="deals-scan-amazon"
              className="h-9 rounded-sm bg-[#D97706] text-gray-900 font-bold uppercase tracking-wider text-xs hover:bg-[#FFAB1F] disabled:opacity-50">
              <Play size={12} weight="fill" className="mr-1" /> SCAN AMAZON
            </Button>
            <Button onClick={() => startScan("amazon", false, { asinRowsOnly: true })} disabled={scanRunning || !data.rows.length}
              data-testid="deals-refresh-buybox"
              title="Re-pull the CURRENT Buy Box only for rows that already have an ASIN. Skips NO ASIN and NOT ON AMAZON rows, ignores the 24h cache. ≈2 Keepa tokens per set."
              className="h-9 rounded-sm bg-transparent border border-[#D97706] text-[#D97706] font-bold uppercase tracking-wider text-xs hover:bg-[#D97706]/10 disabled:opacity-50">
              <ArrowsClockwise size={12} weight="bold" className="mr-1" /> REFRESH BUY BOX
            </Button>
            <Button onClick={() => startScan("amazon_asin")} disabled={scanRunning || !data.rows.length}
              data-testid="deals-scan-amazon-asin"
              title="Resolve missing ASINs only. Skips price fetch — much cheaper on Keepa tokens. Skips deals that already have a verified ASIN."
              className="h-9 rounded-sm bg-transparent border border-[#D97706] text-[#D97706] font-bold uppercase tracking-wider text-xs hover:bg-[#D97706]/10 disabled:opacity-50">
              <Play size={12} weight="fill" className="mr-1" /> ASIN ONLY
            </Button>
            <Button onClick={() => startScan("bricklink")} disabled={scanRunning || !data.rows.length}
              data-testid="deals-scan-bricklink"
              className="h-9 rounded-sm bg-[#0055BF] text-gray-900 font-bold uppercase tracking-wider text-xs hover:bg-[#4FC3F7] disabled:opacity-50">
              <Play size={12} weight="fill" className="mr-1" /> SCAN BRICKLINK
            </Button>
            <Button onClick={() => startScan("pricecharting")} disabled={scanRunning || !data.rows.length}
              data-testid="deals-scan-pricecharting"
              className="h-9 rounded-sm bg-[#7C4DFF] text-gray-900 font-bold uppercase tracking-wider text-xs hover:bg-[#9575CD] disabled:opacity-50">
              <Play size={12} weight="fill" className="mr-1" /> SCAN PRICECHARTING
            </Button>
            {scanRunning && (
              <Button onClick={cancelScan} variant="outline" className="h-9 rounded-sm border-[#FF3333] text-[#FF3333] font-bold uppercase tracking-wider text-xs hover:bg-[#FF3333]/10" data-testid="deals-scan-cancel">
                STOP
              </Button>
            )}
            <div className="flex items-center gap-2 px-3 py-1.5 border border-[#E4E4E0] rounded-sm"
                 title="ON: whole-list scans only hit rows with no data yet for that source (no Buy Box / BL price / PC price). OFF: rescan every row. Ticked-row scans always refresh.">
              <span className="text-xs font-mono text-gray-600">Missing only</span>
              <Switch checked={missingOnly} onCheckedChange={toggleMissingOnly}
                className="data-[state=checked]:bg-[#00A850] h-4 w-7" data-testid="deals-missing-only" />
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 border border-[#E4E4E0] rounded-sm">
              <span className="text-xs font-mono text-gray-600">Continuous</span>
              <Switch checked={scanContinuous} onCheckedChange={setScanContinuous}
                className="data-[state=checked]:bg-[#E3000B] h-4 w-7" data-testid="deals-continuous" />
            </div>
            <Button onClick={load} variant="outline"
              className="h-9 rounded-sm border-[#E4E4E0] bg-[#F6F6F3] text-xs font-mono hover:border-[#E3000B] hover:text-[#E3000B]"
              data-testid="deals-refresh">
              <ArrowsClockwise size={12} className="mr-1" /> REFRESH
            </Button>
            <Button onClick={pushToBoard} disabled={pushingBoard || !data.rows.length} variant="outline"
              title="Mirror this tab's scanned Amazon + BrickLink prices onto the Opportunity Board (rows flip DEMO → LIVE). New scans do this automatically."
              className="h-9 rounded-sm border-[#E4E4E0] bg-[#F6F6F3] text-xs font-mono hover:border-[#00A850] hover:text-[#00A850] disabled:opacity-50"
              data-testid="deals-push-board">
              <ArrowUpRight size={12} className="mr-1" /> {pushingBoard ? "PUSHING…" : "PUSH TO BOARD"}
            </Button>
            <Button
              onClick={approveAllSmart}
              disabled={approvingAll || smartCandidates.length === 0}
              data-testid="deals-approve-all-smart"
              variant="outline"
              className="h-9 rounded-sm border-[#E3000B]/60 bg-[#E3000B]/[0.06] text-[#E3000B] font-bold uppercase tracking-wider text-xs hover:bg-[#E3000B]/[0.14] disabled:opacity-50 ml-auto"
              title={smartCandidates.length ? `Lock the SMART OFFER on ${smartCandidates.length} ${selected.size ? "selected " : ""}row${smartCandidates.length === 1 ? "" : "s"} (skips rows you've adjusted or already approved)` : "No unapproved rows with a Smart Offer"}
            >
              <LockSimple size={12} weight="fill" className="mr-1" /> {approvingAll ? "APPROVING…" : `APPROVE ALL SMART${smartCandidates.length ? ` (${smartCandidates.length})` : ""}`}
            </Button>
            <Button
              onClick={() => setOffersOpen(true)}
              disabled={!data.rows.length}
              data-testid="deals-make-offers"
              className="h-9 rounded-sm bg-[#00A850] text-white font-bold uppercase tracking-wider text-xs hover:bg-[#00C060] disabled:opacity-50"
              title="Preview + export a client-facing offer sheet (CSV) with set #, title, and offer only"
            >
              <Receipt size={12} weight="fill" className="mr-1" /> MAKE OFFERS
            </Button>
            <ColumnsMenu allCols={ALL_COLS} visible={visibleCols} toggle={toggleCol} />
            {sort.key && (
              <button onClick={() => { localStorage.removeItem("deals_sort"); setSort({ key: null, dir: "asc" }); }}
                data-testid="deals-sort-clear"
                title="Clear sort"
                className="h-9 px-3 rounded-sm border border-[#E3000B]/50 bg-[#E3000B]/[0.06] font-mono text-[10px] uppercase text-[#E3000B] hover:bg-[#E3000B]/[0.12] inline-flex items-center gap-1">
                SORT: {sort.key.replace("_", " ")} {sort.dir === "asc" ? "▲" : "▼"} <X size={10} weight="bold" />
              </button>
            )}
          </div>

          {/* Offer Rules — knobs that drive the SUGGESTED column & MAKE OFFERS output */}
          <OfferRulesPanel settings={settings} onSaved={(s) => { setSettings(s); refresh(); }} />

          {/* Selection bar — appears when user ticks rows below */}
          {selected.size > 0 && (
            <div className="border border-[#E3000B]/40 bg-[#E3000B]/[0.06] rounded-sm p-3 flex flex-wrap items-center gap-2" data-testid="deals-selection-bar">
              <span className="font-mono text-xs text-[#E3000B] mr-2" data-testid="deals-selection-count">
                {selected.size} SELECTED
              </span>
              <Button onClick={() => startScan("amazon", true)} disabled={scanRunning}
                data-testid="deals-scan-selected-amazon"
                className="h-8 rounded-sm bg-[#D97706] text-gray-900 font-bold uppercase tracking-wider text-[10px] hover:bg-[#FFAB1F] disabled:opacity-50">
                <Play size={10} weight="fill" className="mr-1" /> SCAN AMAZON
              </Button>
              <Button onClick={() => startScan("amazon_asin", true)} disabled={scanRunning}
                data-testid="deals-scan-selected-asin"
                className="h-8 rounded-sm bg-transparent border border-[#D97706] text-[#D97706] font-bold uppercase tracking-wider text-[10px] hover:bg-[#D97706]/10 disabled:opacity-50">
                <Play size={10} weight="fill" className="mr-1" /> ASIN ONLY
              </Button>
              <Button onClick={() => startScan("bricklink", true)} disabled={scanRunning}
                data-testid="deals-scan-selected-bricklink"
                className="h-8 rounded-sm bg-[#0055BF] text-gray-900 font-bold uppercase tracking-wider text-[10px] hover:bg-[#4FC3F7] disabled:opacity-50">
                <Play size={10} weight="fill" className="mr-1" /> SCAN BRICKLINK
              </Button>
              <Button onClick={() => startScan("pricecharting", true)} disabled={scanRunning}
                data-testid="deals-scan-selected-pc"
                className="h-8 rounded-sm bg-[#7C4DFF] text-gray-900 font-bold uppercase tracking-wider text-[10px] hover:bg-[#9575CD] disabled:opacity-50">
                <Play size={10} weight="fill" className="mr-1" /> SCAN PRICECHARTING
              </Button>
              <Button onClick={clearSelection} variant="outline"
                data-testid="deals-selection-clear"
                className="h-8 rounded-sm border-[#E4E4E0] bg-transparent text-[10px] font-mono uppercase text-gray-600 hover:border-zinc-500">
                CLEAR
              </Button>
            </div>
          )}

          {scanJob && (
            <div className="border border-[#E4E4E0] rounded-sm p-3 font-mono text-[11px]" data-testid="deals-scan-job">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-500">
                  {(scanJob.params?.source || "SCAN").toUpperCase()} · JOB STATUS
                </span>
                <span className={
                  scanJob.status === "completed" ? "text-[#00A850]" :
                  scanJob.status === "failed" ? "text-[#FF3333]" :
                  scanJob.status === "paused" ? "text-[#FF8C00]" :
                  scanJob.status === "waiting_for_tokens" ? "text-[#FF8C00]" : "text-[#E3000B]"
                }>{scanJob.status.toUpperCase().replace(/_/g, " ")}</span>
              </div>
              <div className="w-full h-1.5 bg-[#F6F6F3] border border-[#E4E4E0] rounded-sm overflow-hidden">
                <div className="h-full bg-[#E3000B] transition-all" style={{ width: `${progressPct}%` }} />
              </div>
              <div className="flex justify-between mt-2 text-gray-600">
                <span>{scanJob.progress}/{scanJob.total} processed</span>
                <span className="flex items-center gap-3">
                  <span className="text-[#00A850]" data-testid="deals-scan-with-data">{scanJob.opportunities_found ?? 0} with data</span>
                  <span className="text-[#FF8C00]" data-testid="deals-scan-not-found">
                    {scanJob.params?.not_found ?? 0} {
                      ["amazon", "amazon_asin"].includes(scanJob.params?.source) ? "not on Amazon"
                        : scanJob.params?.source === "bricklink" ? "no BL listing" : "not on PriceCharting"
                    }
                  </span>
                </span>
              </div>
              {scanJob.error && <div className="text-gray-500 mt-1">{scanJob.error}</div>}
            </div>
          )}
        </div>
      </div>

      {/* Table — mirrors Collection layout + BrickLink + PriceCharting columns */}
      <div className="border border-[#E4E4E0] rounded-sm overflow-x-auto bg-[#FFFFFF]">
        <table className="w-full text-xs font-mono">
          <thead className="bg-[#F6F6F3] border-b border-[#E4E4E0]">
            <tr>
              <th className="px-2 py-2 w-8 text-center">
                <input
                  type="checkbox"
                  data-testid="deals-select-all"
                  aria-label="Select all deals"
                  checked={data.rows.length > 0 && data.rows.every(r => selected.has(r.set_number))}
                  ref={el => {
                    if (!el) return;
                    const some = data.rows.some(r => selected.has(r.set_number));
                    const all = data.rows.length > 0 && data.rows.every(r => selected.has(r.set_number));
                    el.indeterminate = some && !all;
                  }}
                  onChange={toggleAll}
                  className="accent-[#E3000B] w-3 h-3 cursor-pointer"
                />
              </th>
              <Th label="SET" sortKey="set" sort={sort} onSort={onSort} />
              {isVisible("name") && <Th label="NAME" sortKey="name" sort={sort} onSort={onSort} />}
              {isVisible("asin") && <Th label="ASIN" sortKey="asin" sort={sort} onSort={onSort} />}
              {isVisible("qty") && <Th label="QTY" align="right" sortKey="qty" sort={sort} onSort={onSort} />}
              {isVisible("paid") && <Th label="PAID" align="right" sortKey="paid" sort={sort} onSort={onSort} />}
              {isVisible("amz_bb") && <Th label="AMZ BUY BOX" align="right" sortKey="amz_bb" sort={sort} onSort={onSort} />}
              {isVisible("fba_fbm") && <Th label="FBA / FBM" align="right" />}
              {isVisible("stock") && <Th label="STOCK" align="center" sortKey="stock" sort={sort} onSort={onSort} />}
              {isVisible("rank") && <Th label="RANK" align="right" sortKey="rank" sort={sort} onSort={onSort} />}
              {isVisible("bl_lowest") && <Th label="BL LOWEST" align="right" sortKey="bl_lowest" sort={sort} onSort={onSort} />}
              {isVisible("bl_avg") && <Th label="BL AVG" align="right" sortKey="bl_avg" sort={sort} onSort={onSort} />}
              {isVisible("pc_sealed") && <Th label="PC SEALED" align="right" sortKey="pc_sealed" sort={sort} onSort={onSort} />}
              {isVisible("suggested") && <Th label="SUGGEST / MAX BUY" align="right" sortKey="suggested" sort={sort} onSort={onSort} />}
              {isVisible("smart_offer") && <Th label="SMART OFFER" align="right" sortKey="smart_offer" sort={sort} onSort={onSort} />}
              {isVisible("unit_pl") && <Th label="UNIT P&L" align="right" sortKey="unit_pl" sort={sort} onSort={onSort} />}
              {isVisible("approve") && <Th label="APPROVE" align="center" sortKey="approve" sort={sort} onSort={onSort} />}
              {isVisible("last_scan") && <Th label="LAST SCAN" align="center" sortKey="last_scan" sort={sort} onSort={onSort} />}
              <Th label="" />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={17} className="p-6 text-center text-gray-500">Loading…</td></tr>
            )}
            {!loading && data.rows.length === 0 && (
              <tr>
                <td colSpan={17} className="p-8 text-center text-gray-500" data-testid="deals-empty">
                  No {tab} deals in "{activeList?.name || "this tab"}" yet. Upload a CSV above to get started.
                </td>
              </tr>
            )}
            {!loading && sortedRows.map(r => (
              <DealRow
                key={r.id}
                r={r}
                tab={tab}
                onDelete={onDelete}
                checked={selected.has(r.set_number)}
                onToggle={() => toggleRow(r.set_number)}
                onDataChanged={refresh}
                isVisible={isVisible}
                onOpenManualPrice={openManualPrice}
                onEditOffer={setOfferEdit}
              />
            ))}
          </tbody>
        </table>
      </div>

      <OfferExportDialog
        open={offersOpen}
        onClose={() => { setOffersOpen(false); refresh(); }}
        rows={data.rows}
        selectedSetNumbers={selected.size > 0 ? Array.from(selected) : null}
      />
      <ManualPriceDialog state={manualState} setState={setManualState} onDataChanged={refresh} />
      <ManualOfferDialog state={offerEdit} setState={setOfferEdit} onSaved={refresh} />
    </div>
  );
}

/**
 * OfferRulesPanel — collapsible editor for the 3 offer-generator rules
 * plus the underlying Amazon fee %, FBA fee, shipping, prep, target ROI.
 * Persists to AppSettings; every save triggers a Deals reload so the
 * SUGGESTED column recomputes.
 */
